import { createFileRoute, notFound } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";
import {
  NOTARY_DOC_LANDINGS,
  getNotaryDocLanding,
  type NotaryDocLanding,
} from "@/content/notary-corpus/documents";

function docFaqsEn(d: NotaryDocLanding) {
  return [
    { q: `What is a ${d.titleEn.toLowerCase()} used for?`, a: d.useCaseEn },
    { q: `What do I need to bring for the ${d.officialEn}?`, a: d.prepareEn.join(" · ") },
    { q: "What exactly does the attorney certify?", a: d.certTypeEn },
    { q: "Why do documents get rejected?", a: d.pitfallsEn.join(" · ") },
    { q: "What happens after notarisation?", a: d.nextStepEn },
    { q: "How long does it take?", a: `${d.turnaroundEn} Fees are based on the number of documents and stamps, and you get a written quote before any work starts.` },
    {
      q: "Can Thailand issue an apostille?",
      a: "No. Thailand is not a party to the Hague Apostille Convention, so Thai documents intended for use abroad follow the legalisation route: certification by a notarial services attorney, then legalisation at the Department of Consular Affairs (Ministry of Foreign Affairs), then attestation at the destination country's embassy or consulate in Thailand. Check the current status with the HCCH and the Department of Consular Affairs before you act.",
    },
    {
      q: "Is a Thai notarial services attorney the same as a notary public?",
      a: "Thailand has no notary public office in the common-law sense. Instead, lawyers registered with the Lawyers Council of Thailand under its 2008 regulation act as Notarial Services Attorneys, and their certifications are the form accepted by foreign authorities.",
    },
    {
      q: "Must I attend in person?",
      a: "Yes for signature certification — you sign in the attorney's presence with an original passport or Thai ID. For a certified true copy, the original document must be produced.",
    },
  ];
}

export const Route = createFileRoute("/en/notary/document/$doc")({
  loader: ({ params }) => {
    const d = getNotaryDocLanding(params.doc);
    if (!d) throw notFound();
    return { d };
  },
  component: NotaryDocPageEN,
  head: ({ params, loaderData }) => {
    if (!loaderData) {
      return { meta: [{ title: "Document type not found" }, { name: "robots", content: "noindex" }] };
    }
    const site = getActiveSiteUrl();
    const { d } = loaderData;
    const title = `${d.titleEn} in Thailand (2026)`;
    const description = `${d.titleEn} by a registered Thai notarial services attorney — what to prepare, the legalisation steps that follow, and turnaround.`;
    return {
      ...pageHeadForProvince({
        title,
        description,
        path: `/en/notary/document/${params.doc}`,
        type: "article",
        locale: "en_US",
        alternates: [{ lang: "th", path: `/notary/document/${params.doc}` }],
        keywords: [
          d.titleEn,
          `notary public thailand ${d.slug.replace(/-/g, " ")}`,
          "notarial services attorney thailand",
          "document legalisation thailand",
          "notary phuket",
        ],
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "Home", url: `${site}/en` },
              { name: "Notary Public", url: `${site}/en/notary` },
              { name: d.titleEn, url: `${site}/en/notary/document/${params.doc}` },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(docFaqsEn(d))) },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema(`/en/notary/document/${params.doc}`)) },
      ],
    };
  },
});

function NotaryDocPageEN() {
  const { d } = Route.useLoaderData();
  const faqs = docFaqsEn(d);
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-4xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/en" className="hover:underline">Home</a> <span>/</span>{" "}
          <a href="/en/notary" className="hover:underline">Notary Public</a> <span>/</span>{" "}
          <span>{d.titleEn}</span>
        </nav>

        <h1 className="mb-3 text-3xl font-bold md:text-4xl">{d.titleEn} — Thailand (2026)</h1>
        <p className="mb-6 text-lg text-muted-foreground" data-speakable="true">
          Document: <strong>{d.officialEn}</strong> — certification provided: {d.certTypeEn}
        </p>

        <div className="mb-8 flex flex-wrap gap-2 text-sm">
          <a href="/contact" className="rounded-md bg-primary px-4 py-2 font-medium text-primary-foreground">Get a free quote →</a>
          <a href="/en/notary/faq" className="rounded-md border px-4 py-2 font-medium hover:bg-muted">Notary FAQ</a>
          <a href={`/notary/document/${d.slug}`} className="rounded-md border px-4 py-2 text-muted-foreground hover:bg-muted">ภาษาไทย</a>
        </div>

        <section className="mb-10">
          <h2 className="mb-3 text-xl font-bold">What it is used for</h2>
          <p className="text-sm leading-relaxed text-muted-foreground">{d.useCaseEn}</p>
        </section>

        <section className="mb-10 rounded-lg border bg-card p-6">
          <h2 className="mb-3 text-xl font-bold">What to prepare</h2>
          <ul className="list-disc space-y-1 pl-6 text-sm text-muted-foreground">
            {d.prepareEn.map((x: string, i: number) => <li key={i}>{x}</li>)}
          </ul>
          <p className="mt-4 text-sm text-muted-foreground"><strong>Turnaround:</strong> {d.turnaroundEn}</p>
        </section>

        <section className="mb-10">
          <h2 className="mb-3 text-xl font-bold">Why documents get rejected</h2>
          <ul className="list-disc space-y-1 pl-6 text-sm text-muted-foreground">
            {d.pitfallsEn.map((x: string, i: number) => <li key={i}>{x}</li>)}
          </ul>
        </section>

        <section className="mb-10 rounded-lg border bg-muted/20 p-6">
          <h2 className="mb-3 text-xl font-bold">Next step after notarisation</h2>
          <p className="text-sm leading-relaxed text-muted-foreground">{d.nextStepEn}</p>
        </section>

        <section className="mb-10">
          <h2 className="mb-3 text-xl font-bold">FAQ — {d.titleEn}</h2>
          <div className="space-y-3">
            {faqs.map((f, i) => (
              <details key={i} className="rounded-lg border bg-card p-4" {...(i < 3 ? { open: true } : {})}>
                <summary className="cursor-pointer text-sm font-semibold">{f.q}</summary>
                <p className="faq-answer mt-2 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="mb-10">
          <h2 className="mb-3 text-xl font-bold">Other documents we notarise</h2>
          <div className="flex flex-wrap gap-2 text-xs">
            {NOTARY_DOC_LANDINGS.filter((x) => x.slug !== d.slug).map((x) => (
              <a key={x.slug} href={`/en/notary/document/${x.slug}`} className="rounded-full border bg-muted/40 px-3 py-1 text-muted-foreground hover:bg-muted">
                {x.titleEn}
              </a>
            ))}
          </div>
        </section>

        <p className="mb-6 text-xs text-muted-foreground">
          Professional framework: Lawyers Council of Thailand regulation on the registration of notarial services attorneys, B.E. 2551 (2008).
          Legalisation steps follow the Department of Consular Affairs, Ministry of Foreign Affairs. Requirements and processing times change —
          verify with the responsible authority before acting.
        </p>

        <FaqSourceNote topic="notary" locale="en" />
      </main>
      <SiteFooter />
    </>
  );
}